import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu } from '@components/ui/Menu/Menu';
import { DiscardModal } from '@components/ui/DiscardModal/DiscardModal';
import { KebabButton } from '@components/ui/KebabButton/KebabButton';
import styles from './MapMenu.module.css';

export interface IMapMenuProps {
  onChangePlan: () => void;
  onClearMap: () => void;
  /** Extra class on the trigger — lets the SiteMap toolbar frame this kebab
   *  like its own icon-button cluster. Without it, the bare KebabButton. */
  triggerClassName?: string;
}

type PendingConfirm = 'clear' | null;

/**
 * SiteMap toolbar's `⋯` — "Change base plan…" and "Clear map…". Per spec §4.2
 * the change-plan item hands off to the caller DIRECTLY (`onChangePlan`) —
 * SiteMap owns the picker→confirm flow that follows, since the confirm needs
 * to name the concrete floor being switched to, which this menu never knows.
 * "Clear map…" stays a same-component confirm — it has no picker step to
 * chain into. "Export snapshot" is deliberately omitted (spec §8 defers it).
 */
export function MapMenu({ onChangePlan, onClearMap, triggerClassName }: IMapMenuProps) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingConfirm>(null);

  return (
    <>
      <Menu.Root>
        <Menu.Trigger render={<KebabButton className={triggerClassName} aria-label={t('logistics.siteMap.more')} />} />
        <Menu.Portal>
          <Menu.Positioner side="bottom" align="end" sideOffset={4}>
            <Menu.Popup>
              <Menu.Item onClick={onChangePlan}>{t('logistics.siteMap.menu.changePlan')}</Menu.Item>
              <Menu.Item className={styles.destructiveItem} onClick={() => setPending('clear')}>
                {t('logistics.siteMap.menu.clearMap')}
              </Menu.Item>
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>

      <DiscardModal
        open={pending === 'clear'}
        title={t('logistics.siteMap.clearTitle')}
        description={t('logistics.siteMap.clearBody')}
        confirmLabel={t('logistics.siteMap.clearConfirm')}
        cancelLabel={t('logistics.siteMap.cancel')}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          setPending(null);
          onClearMap();
        }}
      />
    </>
  );
}
